import { Colors } from "../../config/DesignConstants";
import { rBool } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { BaseEnemy } from "./BaseEnemy";
import { SteeringBehaviors } from "./util/SteeringBehaviors";

//=============================================================================
// INTERCEPTOR CONFIGURATION - All tweakable values in one place
//=============================================================================

const INTERCEPTOR_CONFIG = {
  // Basic stats
  health: 1, // Very fragile - one hit kills
  killPoints: 5, // Low points value
  mass: 0.3, // Very light and fast

  // Visual
  color: Colors.RED,
  secondaryColor: Colors.DARK_RED,
  strokeWidth: 1,
  size: 4,
  shape: [V(-4, -4), V(4, 0), V(-4, 4)], // Small triangle

  // Movement speeds
  maxSpeed: 120, // Maximum speed
  cruiseSpeed: 100, // Target cruising speed
  approachSpeed: 96, // Speed when approaching (80% of max)

  // Force strengths
  approachForce: 150, // Force when approaching center
  wanderForce: 80, // Force for wandering movement
  speedBoostForce: 120, // Force to maintain high speed
  shieldAvoidanceForce: 160, // Very strong shield avoidance
  separationForce: 40, // Separation from other interceptors
  containmentForce: 32, // Force to stay in play area

  // Distances and radii
  splitDistance: 150, // Distance from center when phase changes
  shieldAvoidanceRadius: 70, // Shield avoidance radius
  wanderContainmentRadius: 150, // Keep interceptors within this distance
  separationRadius: 25, // Minimum separation distance
  waypointReachDistance: 10, // Distance to consider waypoint reached

  // Behavior parameters
  velocityDamping: -0.1, // Minimal damping for high speed
  wanderRadius: 30, // Wander circle radius
  wanderDistance: 50, // Wander circle distance ahead
  flockRadius: 120, // Distance to consider neighbors
} as const;

type InterceptorPhase = "approaching" | "splitting";

//=============================================================================
// INTERCEPTOR ENEMY CLASS - Fast aggressive swarm behavior
//=============================================================================

export class InterceptorEnemy extends BaseEnemy {
  // Behavior state
  private phase: InterceptorPhase = "approaching";
  private waypoints?: V2d[];
  private currentWaypointIndex = 0;
  private splitDirection?: V2d;

  // Wander state (required by SteeringBehaviors)
  wanderAngle?: number;

  constructor(position: V2d, waypoints?: V2d[]) {
    super(position, {
      health: INTERCEPTOR_CONFIG.health,
      killPoints: INTERCEPTOR_CONFIG.killPoints,
      mass: INTERCEPTOR_CONFIG.mass,
      visualConfig: {
        points: [...INTERCEPTOR_CONFIG.shape],
        color: INTERCEPTOR_CONFIG.color,
        secondaryColor: INTERCEPTOR_CONFIG.secondaryColor,
        strokeWidth: INTERCEPTOR_CONFIG.strokeWidth,
      },
      movement: {
        maxSpeed: INTERCEPTOR_CONFIG.maxSpeed,
        acceleration: INTERCEPTOR_CONFIG.approachForce,
        damping: INTERCEPTOR_CONFIG.velocityDamping,
      },
    });

    this.waypoints = waypoints;

    const toCenter = V(0, 0).sub(V(this.body.position)).normalize();
    this.body.velocity = toCenter.imul(INTERCEPTOR_CONFIG.maxSpeed * 0.5);

    // Customize aim spring for more responsive rotation
    this.aimSpring.stiffness = 15;
    this.aimSpring.damping = 0.8;
  }

  onTick(): void {
    this.applyVelocityDamping();
    this.updatePhase();

    switch (this.phase) {
      case "approaching":
        this.handleApproachPhase();
        break;
      case "splitting":
        this.handleSplittingPhase();
        break;
    }

    this.aimWithMovement();
  }

  private updatePhase(): void {
    const position = V(this.body.position);
    const distanceFromCenter = position.magnitude;

    if (
      this.phase === "approaching" &&
      distanceFromCenter < INTERCEPTOR_CONFIG.splitDistance
    ) {
      this.initiateSplit();
    }
  }

  private initiateSplit(): void {
    this.phase = "splitting";

    // Pick a random perpendicular direction to split
    const position = V(this.body.position);
    const velocity = V(this.body.velocity);
    const toCenter = V(0, 0).sub(position).normalize();

    // Randomly choose left or right split direction
    this.splitDirection = rBool(0.5)
      ? toCenter.rotate90cw()
      : toCenter.rotate90ccw();

    // Blend split direction with current velocity for smooth transition
    const blendedDirection = velocity
      .normalize()
      .lerp(this.splitDirection, 0.5)
      .normalize();

    // Initialize wander angle for smooth transition
    this.wanderAngle = blendedDirection.angle;
  }

  //===========================================================================
  // APPROACH PHASE - Follow waypoints or go straight to center
  //===========================================================================

  private handleApproachPhase(): void {
    if (!this.body) return;

    const position = V(this.body.position);

    // Follow waypoints if available, otherwise go straight to center
    if (this.waypoints && this.currentWaypointIndex < this.waypoints.length) {
      this.followWaypoints(position);
    } else {
      this.approachCenter(position);
    }
  }

  private followWaypoints(position: V2d): void {
    const targetWaypoint = this.waypoints![this.currentWaypointIndex];
    const toWaypoint = targetWaypoint.sub(position);
    const distanceToWaypoint = toWaypoint.magnitude;

    // Move to next waypoint if close enough
    if (distanceToWaypoint < INTERCEPTOR_CONFIG.waypointReachDistance) {
      this.currentWaypointIndex++;
    }

    // Use seek behavior to maintain speed through waypoints
    const result = SteeringBehaviors.seek(
      this,
      targetWaypoint,
      INTERCEPTOR_CONFIG.approachForce,
    );

    this.body!.applyForce([result.force.x, result.force.y]);
  }

  private approachCenter(position: V2d): void {
    // Use base class seek method to approach center
    const centerPos = V(0, 0);
    this.seekPosition(centerPos);
  }

  //===========================================================================
  // SPLITTING PHASE - Aggressive swarm behavior with multiple forces
  //===========================================================================

  private handleSplittingPhase(): void {
    if (!this.body) return;

    const position = V(this.body.position);
    const velocity = V(this.body.velocity);
    const currentSpeed = velocity.magnitude;
    let totalForce = V(0, 0);

    // 1. Speed maintenance - highest priority
    totalForce = totalForce.add(
      this.calculateSpeedBoostForce(velocity, currentSpeed),
    );

    // 2. Aggressive wandering for unpredictability
    totalForce = totalForce.add(this.calculateWanderForce());

    // 3. Shield avoidance - critical for survival
    totalForce = totalForce.add(this.calculateShieldAvoidanceForce(position));

    // 4. Separation from other interceptors
    totalForce = totalForce.add(this.calculateSeparationForce());

    // 5. Containment to stay in play area
    totalForce = totalForce.add(this.calculateContainmentForce(position));

    this.body.applyForce([totalForce.x, totalForce.y]);
  }

  private calculateSpeedBoostForce(velocity: V2d, currentSpeed: number): V2d {
    // Maintain high speed in current direction
    if (currentSpeed < INTERCEPTOR_CONFIG.cruiseSpeed) {
      const currentDirection =
        velocity.magnitude > 0.1 ? velocity.normalize() : V(1, 0);
      return currentDirection.mul(INTERCEPTOR_CONFIG.speedBoostForce * 1.5);
    }
    return V(0, 0);
  }

  private calculateWanderForce(): V2d {
    // Aggressive wander for unpredictable movement
    const wanderResult = SteeringBehaviors.wander(
      this,
      INTERCEPTOR_CONFIG.wanderForce * 0.8,
      INTERCEPTOR_CONFIG.wanderRadius,
      INTERCEPTOR_CONFIG.wanderDistance,
      INTERCEPTOR_CONFIG.wanderContainmentRadius,
    );
    return wanderResult.force;
  }

  private calculateShieldAvoidanceForce(position: V2d): V2d {
    // Strong shield avoidance - highest priority
    const distanceFromCenter = position.magnitude;
    if (distanceFromCenter < INTERCEPTOR_CONFIG.shieldAvoidanceRadius) {
      const awayFromCenter = position.normalize();
      const weight =
        (INTERCEPTOR_CONFIG.shieldAvoidanceRadius - distanceFromCenter) /
        INTERCEPTOR_CONFIG.shieldAvoidanceRadius;
      // Very strong exponential avoidance force
      return awayFromCenter.mul(
        weight * weight * INTERCEPTOR_CONFIG.shieldAvoidanceForce * 2,
      );
    }
    return V(0, 0);
  }

  private calculateSeparationForce(): V2d {
    // Minimal separation - only avoid very close neighbors
    const nearbyInterceptors = this.getNearbyInterceptors();
    if (nearbyInterceptors.length > 0) {
      const separationResult = SteeringBehaviors.separate(
        this,
        nearbyInterceptors,
        INTERCEPTOR_CONFIG.separationForce,
        INTERCEPTOR_CONFIG.separationRadius,
      );
      return separationResult.force;
    }
    return V(0, 0);
  }

  private calculateContainmentForce(position: V2d): V2d {
    // Containment - but allow them to get closer to edges
    const distanceFromCenter = position.magnitude;
    if (distanceFromCenter > INTERCEPTOR_CONFIG.wanderContainmentRadius * 0.8) {
      const centerPos = V(0, 0);
      const containmentResult = SteeringBehaviors.seek(
        this,
        centerPos,
        INTERCEPTOR_CONFIG.containmentForce,
      );
      return containmentResult.force;
    }
    return V(0, 0);
  }

  //===========================================================================
  // HELPER METHODS - Support functions for behavior
  //===========================================================================

  private applyVelocityDamping(): void {
    // Use base class damping method
    this.applyDamping();
  }

  private getNearbyInterceptors(): BaseEnemy[] {
    if (!this.game) return [];

    const position = V(this.body?.position || [0, 0]);
    const nearbyInterceptors: BaseEnemy[] = [];

    // Get all interceptor enemies from the game
    const interceptors = this.game.entities
      .getTagged("enemy")
      .filter(
        (entity) =>
          entity.tags.includes("enemy") &&
          entity !== this &&
          (entity as BaseEnemy).body,
      ) as BaseEnemy[];

    for (const interceptor of interceptors) {
      if (!interceptor.body) continue;

      const interceptorPos = V(interceptor.body.position);
      const distance = position.sub(interceptorPos).magnitude;

      if (distance < INTERCEPTOR_CONFIG.flockRadius) {
        nearbyInterceptors.push(interceptor);
      }
    }

    return nearbyInterceptors;
  }
}
